const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export type PortalRoleResult = 'ok' | 'denied' | 'unknown';

type GetToken = () => Promise<string | null | undefined>;

// One identity-based wrong-portal check per session, shared by SignIn (post-
// auth) and ProtectedRoute (deep links). This is the ONLY place "wrong
// portal" is decided — apiFetch deliberately no longer infers it from
// endpoint 403s, because a rank-gated 403 (e.g. a CLASSROOM_VIEWER hitting an
// ADMIN+ endpoint) is a normal partial-authorization answer for a legitimate
// school-admin user, not a sign they belong on another portal.
//
// Keyed by Clerk user id so an account switch re-checks; cached so the
// profile fetch happens once per session, not on every route mount.
let cache: { userId: string; promise: Promise<PortalRoleResult> } | null = null;

/**
 * Resolve whether the signed-in user belongs on this portal.
 *  - 'ok'      → server role is SCHOOL_ADMIN
 *  - 'denied'  → server says a different role (wrong portal — sign them out)
 *  - 'unknown' → couldn't tell (network error, 401 mid-refresh, missing role).
 *                Treat as ok: the API's guards are the real boundary, and we
 *                never lock a legitimate admin out over a transient failure.
 *
 * Uses /students/profile/me, which also runs the server's staff-invite
 * self-heal — so a just-invited teacher is elevated by the very call that
 * checks them.
 */
export function checkPortalRole(userId: string, getToken: GetToken): Promise<PortalRoleResult> {
  if (!cache || cache.userId !== userId) {
    cache = {
      userId,
      promise: (async (): Promise<PortalRoleResult> => {
        try {
          const token = await getToken();
          if (!token) return 'unknown';
          const res = await fetch(`${API_BASE_URL}/v1/students/profile/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) return 'unknown';
          const profile = await res.json();
          const role = profile?.role;
          if (!role) return 'unknown';
          return role === 'SCHOOL_ADMIN' ? 'ok' : 'denied';
        } catch {
          return 'unknown';
        }
      })(),
    };
  }
  return cache.promise;
}

/**
 * Drop the cached verdict. Called before signing out a denied user so that
 * if their role is fixed (e.g. an admin elevates them) their next sign-in
 * re-checks instead of replaying the stale denial.
 */
export function clearPortalRoleCache(): void {
  cache = null;
}

// The denied explanation used to travel as /signin?denied=1 on the signOut
// redirectUrl, but Clerk's dev-instance signOut round-trips through
// accounts.dev and drops the query string — the user landed on a bare
// /signin with no explanation. A sessionStorage flag survives that redirect
// (same tab), so the banner reliably shows. The ?denied=1 query still works
// for direct links.
const DENIED_FLAG_KEY = 'hypebook.schoolAdmin.deniedAccess';

/** Set just before signing out a wrong-portal user. */
export function markPortalDenied(): void {
  try {
    sessionStorage.setItem(DENIED_FLAG_KEY, '1');
  } catch {
    // Storage unavailable — the banner is best-effort.
  }
}

/** Read-and-clear, so the banner shows once and doesn't stick around. */
export function consumePortalDenied(): boolean {
  try {
    const denied = sessionStorage.getItem(DENIED_FLAG_KEY) === '1';
    sessionStorage.removeItem(DENIED_FLAG_KEY);
    return denied;
  } catch {
    return false;
  }
}
